import {
  Action,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import SupportedLocale from '@/types/SupportedLocale';
import SearchDefaults from '@/types/SearchDefaults';
import SupportedCountry from '@/types/SupportedCountry';
import SupportedLayout from '@/types/SupportedLayout';
import BaseState from '@/store/states/Base';
import SupportedContext from '@/types/SupportedContext';
import router from '@/router';
import { BaseGetter, FilterGetter, SearchGetter } from '@/store/getters';
import { BaseAction, GeneralAction } from '@/store/actions';
import QueryString from 'qs';
import InitDataExtended from '@/models/InitDataExtended';
import Vue from 'vue';

enum Mutators {
  HandleInit = 'base.handleInit',
  HandleSwitchActiveLayout = 'base.handleSwitchActiveLayout',
  HandleChangeLoadingRequests = 'base.handleChangeLoadingRequests',
  HandleAddError = 'base.handleAddError',
  HandleAddWarning = 'base.handleAddWarning',
  HandleClearErrors = 'base.handleClearErrors',
  HandleClearWarnings = 'base.handleClearWarnings',
}

@Module
export default class BaseModule extends VuexModule<BaseState> {
  private locale: SupportedLocale;

  private country: SupportedCountry;

  private activeLayout: SupportedLayout;

  private readonly layoutTypes: Array<SupportedLayout>;

  private errors: Map<string, string> = new Map();

  private warnings: Map<string, string> = new Map();

  private activeRequests = 0;

  constructor(module: BaseModule) {
    super(module);

    this.locale = SearchDefaults.locale;
    this.country = SearchDefaults.country;
    this.activeLayout = SearchDefaults.activeLayout;
    this.layoutTypes = Object.values(SupportedLayout);
  }

  get [BaseGetter.ActiveLayout](): SupportedLayout {
    return this.activeLayout;
  }

  get [BaseGetter.IsLayoutSwitchVisible](): boolean {
    return this.context.getters[FilterGetter.ActiveSearchContext] === SupportedContext.ALL;
  }

  get [BaseGetter.LayoutTypes](): Array<SupportedLayout> {
    return this.layoutTypes;
  }

  get [BaseGetter.Country](): SupportedCountry {
    return this.country;
  }

  get [BaseGetter.Locale](): SupportedLocale {
    return this.locale;
  }

  get [BaseGetter.Errors](): Map<string, string> {
    return this.errors;
  }

  get [BaseGetter.Warnings](): Map<string, string> {
    return this.warnings;
  }

  get [BaseGetter.IsLoading](): boolean {
    return this.activeRequests > 0;
  }

  @Mutation
  private [Mutators.HandleInit](payload: {
    locale: SupportedLocale;
    country: SupportedCountry;
  }) {
    this.locale = payload.locale;
    this.country = payload.country;
  }

  @Action
  private [GeneralAction.Init](payload: InitDataExtended) {
    return new Promise<void>((resolve) => {
      this.context.commit(Mutators.HandleInit, {
        locale: payload.locale,
        country: payload.country,
      });
      resolve();
    });
  }

  @Mutation
  private [Mutators.HandleSwitchActiveLayout](payload: { layout: SupportedLayout }) {
    this.activeLayout = payload.layout;
  }

  @Action
  private [BaseAction.SwitchActiveLayout](payload: { layout: SupportedLayout }) {
    this.context.commit(Mutators.HandleSwitchActiveLayout, payload);
  }

  @Action
  private [GeneralAction.SwitchSearchContext](payload: { searchContext: SupportedContext }) {
    if (router.currentRoute.params.searchContext !== payload.searchContext) {
      router.push({
        name: 'Search',
        params: { searchContext: payload.searchContext, country: this.country },
        query: {
          ...{ q: this.context.getters[SearchGetter.SearchTerm] },
          ...this.context.getters[FilterGetter.AppliedFilterOptionsSerialized],
        },
      });
    }
  }

  @Action
  private [GeneralAction.SwitchSearchTerm](payload: { searchTerm: string }) {
    if (router.currentRoute.query.q !== payload.searchTerm) {
      router.push({
        name: 'Search',
        params: { searchContext: router.currentRoute.params.searchContext, country: this.country },
        query: {
          ...{ q: payload.searchTerm },
          ...this.context.getters[FilterGetter.AppliedFilterOptionsSerialized],
        },
      });
    }
  }

  @Action
  private [GeneralAction.SwitchFilters]() {
    const serializedFilters = this.context.getters[FilterGetter.AppliedFilterOptionsSerialized];
    const { q, ...routeSerializedFilters } = router.currentRoute.query;
    if (QueryString.stringify(serializedFilters)
      !== QueryString.stringify(routeSerializedFilters)
    ) {
      router.push({
        name: 'Search',
        params: { searchContext: router.currentRoute.params.searchContext, country: this.country },
        query: {
          ...{ q: this.context.getters[SearchGetter.SearchTerm] },
          ...serializedFilters,
        },
      });
    }
  }

  @Mutation
  private [Mutators.HandleChangeLoadingRequests](payload: { with: number }) {
    Vue.set(this, 'activeRequests', this.activeRequests + payload.with);
  }

  @Action
  private [BaseAction.ChangeActiveRequests](payload: { with: number }): Promise<void> {
    return new Promise<void>((resolve) => {
      this.context.commit(Mutators.HandleChangeLoadingRequests, payload);
      resolve();
    });
  }

  @Mutation
  private [Mutators.HandleAddError](payload: { code: string }) {
    this.errors.set(payload.code, payload.code);
    Vue.set(this, 'errors', new Map(this.errors));
  }

  @Action
  private [BaseAction.AddError](payload: { code: string }): Promise<void> {
    return new Promise<void>((resolve) => {
      this.context.commit(Mutators.HandleAddError, payload);
      resolve();
    });
  }

  @Mutation
  private [Mutators.HandleAddWarning](payload: { code: string }) {
    this.warnings.set(payload.code, payload.code);
    Vue.set(this, 'warnings', new Map(this.warnings));
  }

  @Action
  private [BaseAction.AddWarning](payload: { code: string }): Promise<void> {
    return new Promise<void>((resolve) => {
      this.context.commit(Mutators.HandleAddWarning, payload);
      resolve();
    });
  }

  @Mutation
  private [Mutators.HandleClearErrors]() {
    Vue.set(this, 'errors', new Map());
  }

  @Action
  private [BaseAction.ClearErrors](): Promise<void> {
    return new Promise<void>((resolve) => {
      this.context.commit(Mutators.HandleClearErrors);
      resolve();
    });
  }

  @Mutation
  private [Mutators.HandleClearWarnings]() {
    Vue.set(this, 'warnings', new Map());
  }

  @Action
  private [BaseAction.ClearWarnings](): Promise<void> {
    return new Promise<void>((resolve) => {
      this.context.commit(Mutators.HandleClearWarnings);
      resolve();
    });
  }
}
